
import { Component, ContentChildren, ElementRef, forwardRef, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID, QueryList } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import * as moment_tz from 'moment-timezone';
import { Subscription } from 'rxjs';
import { ErrorMessages } from '../../common/constants';
import { Alert, Thing } from '../../model';
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from '../../service/context.service';
import { DateRangeName, DateRangeService, PeriodVariable } from '../../service/date-range.service';
import { FieldService } from '../../service/field.service';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { MetricDetailComponent } from '../../shared/component';
import { ErrorUtility } from '../../utility/error-utility';
import { EventListWidgeService } from '../alert-list-v2/event-list/event-list-widget.service';
import { AmChart5Component } from '../amchart5/am-chart5.component';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { ThingListWidgetV2Service } from '../thing-list/thing-list-widget-v2.service';
import { DailySummaryGridWidgetService } from './daily-summary-grid-widget.service';

@Component({
    selector: 'daily-summary-grid-widget',
    template: require('./daily-summary-grid-widget.component.html'),
    styles: [require('./daily-summary-grid-widget.component.css')],
    providers: [ThingListWidgetV2Service, DailySummaryGridWidgetService, EventListWidgeService]
})
export class DailySummaryGridWidgetComponent extends AmChart5Component implements OnInit, OnDestroy {

    @Input() title: string;

    @Input() description: string;

    @Input() config: DailySummaryGridWidgetConfig;

    @Input() inputs: ReferenceInput;

    @ContentChildren(MetricDetailComponent) metricComponents: QueryList<MetricDetailComponent>;

    locale: string;
    timezone: string;
    loadedData: boolean;
    error: string;
    isDataEmpty: boolean;
    selectedData: { events: Alert[], metricData: MetricData[], timestamp: number, metricDetailsArray: MetricDetails[] };
    detailsTitle: string;

    private thing: Thing;
    private range: DateRange<moment.Moment>;
    private periodFieldRefTimeoutId: any;
    private advancedSearchTimeoutId: any;
    private periodFieldSubscription: Subscription;
    private series: any;
    private xAxis: any;
    private yAxis: any;
    private chart: any;
    private eventAdvancedSearchBody: any;
    private selectedChartCell: any;
    private selectedXAxisElement: any;
    private eventNamesInput: string[];
    private metricGroupNamesInput: string[];
    private chartData: ChartData[];
    private advancedSearchInitCompleted: boolean;
    private cellHeight: number = 48;
    private _unregisterPrintResize: (() => void) | null = null;

    constructor(
        @Inject(PLATFORM_ID) platformId: Object,
        @Inject(forwardRef(() => NgZone)) zone: NgZone,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => DateRangeService)) private dateRangeService: DateRangeService,
        @Inject(forwardRef(() => FieldService)) protected fieldService: FieldService,
        @Inject(forwardRef(() => DailySummaryGridWidgetService)) protected widgetService: DailySummaryGridWidgetService,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => ElementRef)) private elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private widgetPrintResizeCoordinator: WidgetPrintResizeCoordinatorService
    ) {
        super(platformId, zone);
    }

    ngOnInit(): void {
        this.thing = this.thingContextService.getCurrentThing();
        this.timezone = this.contextService.getTimezone();
        this.locale = this.authenticationService.getLocale();
        this.widgetService.styleElement = this.elementRef.nativeElement;
        if (this.config?.defaultPeriod) {
            const daterange = this.dateRangeService.getCustomDateRangeByName(this.config?.defaultPeriod);
            if (daterange) {
                this.range = new DateRange(daterange.range.start, daterange.range.end);
            }
        }
        this.eventNamesInput = this.config?.eventNames ? this.config?.eventNames.split(',') : null;
        this.metricGroupNamesInput = this.config?.metricGroupNames ? this.config?.metricGroupNames.split(',') : null;
        if (this.eventNamesInput?.length > 0) {
            this.config.eventQuery = [{ property: 'name', predicate: 'eq', value: this.config?.eventNames }];
            if (this.inputs) {
                this.inputs.eventQueryRef = null;
            }
        }
        this._unregisterPrintResize = this.widgetPrintResizeCoordinator.subscribeElement(this.elementRef, () => this.updateBeforePrint());
    }

    ngOnDestroy(): void {
        this._unregisterPrintResize?.();
        this._unregisterPrintResize = null;
        super.ngOnDestroy();
        this.unsubscribeFromPeriodField();
        this.clearAdvancedSearchTimeout();
    }

    private subscribeToPeriodField(): void {
        if (this.inputs?.periodRef) {
            this.periodFieldRefTimeoutId = setTimeout(() => {
                console.error("Unresolved periodRef");
                this.waitForAdvancedSearchInitAndPerformLoadChartData();
            }, 5000);
            this.periodFieldSubscription = this.fieldService.subscribeToFields([this.inputs?.periodRef]).subscribe(fieldsMap => {
                const period: PeriodVariable = fieldsMap ? fieldsMap[this.inputs?.periodRef] : null;
                if (period != null) {
                    this.clearPeriodFieldRefTimeout();
                    this.range = new DateRange(moment_tz.tz(period.start, this.timezone), moment_tz.tz(period.end, this.timezone));
                    this.waitForAdvancedSearchInitAndPerformLoadChartData();
                }
            });
        }
    }

    private waitForAdvancedSearchInitAndPerformLoadChartData(): void {
        if (this.inputs?.eventQueryRef || this.config?.eventQuery) {
            this.clearAdvancedSearchTimeout();
            if (this.advancedSearchInitCompleted) {
                this.loadChartData();
            } else {
                this.advancedSearchTimeoutId = setTimeout(() => this.waitForAdvancedSearchInitAndPerformLoadChartData(), 50);
            }
        } else {
            this.loadChartData();
        }
    }

    private clearPeriodFieldRefTimeout(): void {
        if (this.periodFieldRefTimeoutId) {
            clearTimeout(this.periodFieldRefTimeoutId);
            this.periodFieldRefTimeoutId = null;
        }
    }

    private clearAdvancedSearchTimeout(): void {
        if (this.advancedSearchTimeoutId) {
            clearTimeout(this.advancedSearchTimeoutId);
            this.advancedSearchTimeoutId = null;
        }
    }

    private unsubscribeFromPeriodField(): void {
        if (this.inputs?.periodRef) {
            this.fieldService.unsubscribeFromFields([this.inputs?.periodRef]);
            this.periodFieldSubscription.unsubscribe();
        }
        this.clearPeriodFieldRefTimeout();
    }

    getChartId(): string {
        return 'daily-summary-grid-widget';
    }

    initChart(): void {
        if (this.thing) {
            // Create chart
            // https://www.amcharts.com/docs/v5/charts/xy-chart/
            this.chart = this.root.container.children.push(this.am5xy.XYChart.new(this.root, {
                panX: true,
                panY: false,
                paddingLeft: 0,
                layout: this.root.verticalLayout
            }));

            // Create axes and their renderers
            let yRenderer = this.am5xy.AxisRendererY.new(this.root, {
                visible: false,
                inversed: true,
                minorGridEnabled: true
            });

            yRenderer.grid.template.set("visible", false);
            yRenderer.labels.template.setAll({
                tooltipText: "{categoryLabel}",
                oversizedBehavior: "truncate"
            });
            yRenderer.labels.template.setup = (target) => {
                target.setAll({
                    background: this.am5.Rectangle.new(this.root, {
                        fill: this.am5.color(0x000000),
                        fillOpacity: 0
                    }),
                    minWidth: 80
                });
            };
            yRenderer.labels.template.adapters.add("text", function (text, target) {
                if (target.dataItem && target.dataItem.dataContext) {
                    return target.dataItem.dataContext.categoryLabel;
                }
                return text;
            });

            this.yAxis = this.chart.yAxes.push(this.am5xy.CategoryAxis.new(this.root, {
                maxDeviation: 0,
                renderer: yRenderer,
                categoryField: "category"
            }));
            this.yAxis.labelsContainer.set("tooltip", this.am5.Tooltip.new(this.root, {
                pointerOrientation: "down"
            }));

            let xRenderer = this.am5xy.AxisRendererX.new(this.root, {
                visible: false,
                minorGridEnabled: true,
                minGridDistance: 10,
                opposite: true
            });

            xRenderer.grid.template.set("visible", false);

            this.xAxis = this.chart.xAxes.push(this.am5xy.CategoryAxis.new(this.root, {
                renderer: xRenderer,
                categoryField: "dateTimestamp",
                maxDeviation: 0
            }));
            xRenderer.labels.template.adapters.add("text", function (text, target) {
                if (target.dataItem && target.dataItem.dataContext) {
                    return target.dataItem.dataContext.dateLabel;
                }
                return text;
            });

            // Create series
            // https://www.amcharts.com/docs/v5/charts/xy-chart/#Adding_series
            this.series = this.chart.series.push(this.am5xy.ColumnSeries.new(this.root, {
                stroke: this.am5.color(0xffffff),
                clustered: false,
                xAxis: this.xAxis,
                yAxis: this.yAxis,
                categoryXField: "dateTimestamp",
                categoryYField: "category",
                valueField: "value"
            }));

            this.series.columns.template.setAll({
                tooltipText: this.getTooltipText(),
                strokeOpacity: 1,
                strokeWidth: 2,
                width: this.am5.percent(100),
                height: this.am5.percent(100)
            });

            // Customize tooltip settings
            let tooltip = this.am5.Tooltip.new(this.root, {
                getFillFromSprite: false,
                getStrokeFromSprite: false
            });
            tooltip.get("background").setAll({
                fill: this.am5.color(0xffffff),
                fillOpacity: 1,
                stroke: this.am5.color(0x0e1a1d)
            });
            this.series.set("tooltip", tooltip);

            this.series.columns.template.adapters.add("tooltipText", function (text, target) {
                return target.dataItem?.dataContext?.value?.tooltipText ? text : null;
            });

            // Set up heat rules
            // https://www.amcharts.com/docs/v5/concepts/settings/heat-rules/
            this.series.set("heatRules", [{
                target: this.series.columns.template,
                dataField: "value",
                customFunction: function (sprite, min, max, value) {
                    sprite.set("fill", value.color);
                }
            }]);

            // Add scrollbars
            this.chart.set("scrollbarX", this.am5.Scrollbar.new(this.root, {
                orientation: "horizontal",
                visible: false
            }));
            this.chart.zoomOutButton.set("forceHidden", true);
            let scrollbarX = this.chart.get("scrollbarX");
            scrollbarX.startGrip.setAll({
                disabled: true,
                visible: false
            });
            scrollbarX.endGrip.setAll({
                disabled: true,
                visible: false
            });

            // Set zoom on resize and data validation
            this.xAxis.events.on("boundschanged", (ev) => {
                this.zoomChart(ev);
                this.setMaximumCategoryWidth();
            });
            this.xAxis.events.on("datavalidated", (ev) => {
                this.zoomChart(ev);
                this.setMaximumCategoryWidth();
            });
            this.yAxis.events.on("datavalidated", (ev) => {
                this.setMinimumHeight(ev);
            });

            // Add counts
            this.series.bullets.push((root, series, dataItem) => {
                if ((this.config?.showEventCount && dataItem.dataContext.value.highlightCount > 0 && dataItem.dataContext.value.events?.length > 0) ||
                    (this.config?.showMetricHighlightCount && dataItem.dataContext.value.highlightCount > 0 && dataItem.dataContext.value.metricData?.length > 0)) {
                    return this.am5.Bullet.new(root, {
                        sprite: this.am5.Label.new(root, {
                            fill: this.am5.color(0xffffff),
                            populateText: true,
                            centerX: this.am5.p50,
                            centerY: this.am5.p50,
                            fontSize: 16,
                            text: "{value.highlightCount}"
                        })
                    });
                }
            });

            // Cell click event
            this.series.columns.template.adapters.add("cursorOverStyle", function (value, target) {
                return target.dataItem?.dataContext?.value?.highlightCount > 0 ? 'pointer' : null;
            });
            this.series.columns.template.events.on("click", ev => {
                if (ev.target.dataItem?.dataContext?.value?.highlightCount > 0) {
                    this.resetSelectedElementStyle();
                    ev.target.set("opacity", 0.5);
                    this.zone.run(() => {
                        this.selectedChartCell = ev.target;
                        const dateTimestamp = Number(ev.target.dataItem.dataContext.dateTimestamp);
                        this.selectedData = { events: [], metricData: [], timestamp: dateTimestamp, metricDetailsArray: ev.target.dataItem?.dataContext?.value?.metricDetailsArray };
                        if (ev.target.dataItem?.dataContext?.value?.events?.length) {
                            this.selectedData.events = ev.target.dataItem.dataContext.value.events;
                        }
                        if (ev.target.dataItem?.dataContext?.value?.metricData?.length) {
                            this.selectedData.metricData = ev.target.dataItem.dataContext.value.metricData;
                        }
                        let detailsDate: string;
                        const momentDate = moment_tz.tz(dateTimestamp, this.timezone);
                        detailsDate = momentDate.locale(this.locale).format('ll');
                        this.detailsTitle = detailsDate + ' - ' + ev.target.dataItem?.dataContext.categoryLabel;
                    });
                }
            });
            this.xAxis.get("renderer").labels.template.setup = (target) => {
                target.setAll({
                    background: this.am5.Rectangle.new(this.root, {
                        fill: this.am5.color(0x000000),
                        fillOpacity: 0
                    })
                });
            };
            this.xAxis.get("renderer").labels.template.adapters.add("cursorOverStyle", (value, target) => {
                const filteredData = this.chartData?.filter(data => data.dateTimestamp == target.dataItem?.dataContext?.dateTimestamp && data.value?.highlightCount > 0);
                if (filteredData?.length > 0) {
                    return 'pointer';
                } else {
                    target.events.disableType("click");
                    target.events.disableType("pointerover");
                    return null;
                }
            });
            this.xAxis.get("renderer").labels.template.events.on("click", ev => {
                this.zone.run(() => {
                    const filteredData = this.chartData?.filter(data => data.dateTimestamp == ev.target?.dataItem?.dataContext?.dateTimestamp);
                    const filteredHighlightData = filteredData?.filter(data => data.value?.highlightCount > 0);
                    if (filteredHighlightData?.length > 0) {
                        this.resetSelectedElementStyle();
                        ev.target.set("fontWeight", "bold");
                        this.selectedXAxisElement = ev.target;
                        let events: Alert[] = [];
                        let metricData: MetricData[] = [];
                        const metricDetailsArray: MetricDetails[] = [];
                        filteredData.forEach(data => {
                            data.value?.metricDetailsArray?.forEach(m => {
                                metricDetailsArray.push(m);
                            });
                        });
                        filteredHighlightData.forEach(data => {
                            if (data.value?.events?.length) {
                                events = events.concat(data.value.events);
                            }
                            if (data.value?.metricData?.length) {
                                metricData = metricData.concat(data.value.metricData);
                            }
                        });
                        const dateTimestamp = Number(ev.target.dataItem.dataContext.dateTimestamp);
                        this.selectedData = { events: events, metricData: metricData, timestamp: dateTimestamp, metricDetailsArray: metricDetailsArray };
                        const momentDate = moment_tz.tz(dateTimestamp, this.timezone);
                        this.detailsTitle = momentDate.locale(this.locale).format('ll');
                    }
                });
            });
            this.xAxis.get("renderer").labels.template.events.on("pointerover", e => {
                e.target.set("textDecoration", "underline");
            });
            this.xAxis.get("renderer").labels.template.events.on("pointerout", e => {
                e.target.set("textDecoration", null);
            });

            // Set data
            // https://www.amcharts.com/docs/v5/charts/xy-chart/#Setting_data

            if (this.inputs?.periodRef) {
                this.subscribeToPeriodField();
            } else {
                this.waitForAdvancedSearchInitAndPerformLoadChartData();
            }
        }
    }

    private resetSelectedElementStyle(): void {
        this.resetSelectedCellStyle();
        this.resetXAxisLabelStyle();
    }

    private resetSelectedCellStyle() {
        if (this.selectedChartCell) {
            this.selectedChartCell.set("opacity", 1);
            this.selectedChartCell = null;
        }
    }

    private resetXAxisLabelStyle() {
        if (this.selectedXAxisElement) {
            this.selectedXAxisElement.set("fontWeight", "normal");
            this.selectedXAxisElement = null;
        }
    }

    private zoomChart(ev: any): void {
        if (ev.target.dataItems.length) {
            let scrollbarX = this.chart.get("scrollbarX");
            let xWidth = this.xAxis.width();
            const minCellWidth = 48;
            const maxZoomedElements = Math.floor(xWidth / minCellWidth);
            if (ev.target.dataItems.length > maxZoomedElements) {
                scrollbarX.set("visible", true);
                ev.target.zoomToIndexes(ev.target.dataItems.length - maxZoomedElements, ev.target.dataItems.length);
            } else {
                scrollbarX.set("visible", false);
                ev.target.zoomToIndexes(0, ev.target.dataItems.length);
            }
        }
    }

    private setMinimumHeight(ev: any): void {
        if (ev.target.data?.length) {
            setTimeout(() => {
                const isScrollbarVisible = this.chart.get("scrollbarX").get("visible");
                const extraHeight = isScrollbarVisible ? 96 : 71;
                const totalHeight = (ev.target.data?.length * this.cellHeight) + extraHeight;
                this.chart.root.dom.style.height = totalHeight + "px";
            });
        }
    }

    private setMaximumCategoryWidth(): void {
        const width = this.chart.width();
        this.yAxis.get("renderer").labels.template.setAll({ maxWidth: width / 4 });
    }

    private getTooltipText(): string {
        let text = "{value.tooltipText}";
        return text;
    }

    private loadChartData(): void {
        this.loadedData = false;
        this.closeDetails();
        this.loadData().then(data => {
            this.chartData = data.chartData;
            this.zone.run(() => this.loadedData = true);
            this.isDataEmpty = !data.chartData.length;
            if (this.isDataEmpty) {
                this.series.data.setAll([]);
                this.yAxis.data.setAll([]);
                this.xAxis.data.setAll([]);
            } else {
                this.series.data.setAll(data.chartData);

                // Auto-populate X and Y axis category data
                this.yAxis.data.setAll(data.yAxisValues);
                this.xAxis.data.setAll(data.xAxisValues.map((item) => { return { dateTimestamp: item.startTimestamp, dateLabel: this.formatXAxisTimestamp(item.startTimestamp) } }));

                // Make stuff animate on load
                // https://www.amcharts.com/docs/v5/concepts/animations/#Initial_animation
                this.chart.appear(1000, 100);
            }
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private formatXAxisTimestamp(timestamp: number): string {
        const momentDate = moment_tz.tz(timestamp, this.timezone).locale(this.locale);
        if (momentDate.clone().startOf('year').valueOf() == timestamp) {
            return momentDate.format('YYYY');
        } else if (momentDate.clone().startOf('month').valueOf() == timestamp) {
            return momentDate.format('MMM');
        } else {
            return momentDate.format('D');
        }
    }

    private loadData(): Promise<{ chartData: ChartData[], xAxisValues: XAxisValue[], yAxisValues: YAxisValue[] }> {
        if (!this.range) {
            const daterange = this.dateRangeService.getCustomDateRangeByName(DateRangeName.LAST_30_DAYS);
            this.range = new DateRange(daterange.range.start, daterange.range.end);
        }
        const startTimestamp = this.range.start.valueOf();
        const endTimestamp = this.range.end.valueOf();
        this.eventAdvancedSearchBody = this.eventAdvancedSearchBody || {};
        this.eventAdvancedSearchBody.minEndTimestamp = startTimestamp;
        this.eventAdvancedSearchBody.endTimestamp = endTimestamp;   // maxStartTimestamp
        const xAxisValues = this.widgetService.computeXAxisValues(this.range.start.clone(), this.range.end.clone());
        return Promise.all([this.widgetService.loadEventData(this.eventAdvancedSearchBody, xAxisValues, this.config), this.widgetService.loadMetricData(xAxisValues, this.metricComponents, startTimestamp, endTimestamp, this.config)]).then(results => {
            const eventChartData = results[0].data;
            const eventChartCategories = this.filterAxisCategories(results[0].yAxisValues, this.eventNamesInput);
            const metricChartData = results[1].data;
            const metricChartCategories = this.filterAxisCategories(results[1].yAxisValues, this.metricGroupNamesInput);
            return { chartData: eventChartData.concat(metricChartData), xAxisValues: xAxisValues, yAxisValues: eventChartCategories.concat(metricChartCategories) };
        });
    }

    private filterAxisCategories(allYAxisValues: YAxisValue[], categoriesToFilter: string[]): YAxisValue[] {
        let result = [];
        if (categoriesToFilter?.length > 0) {
            categoriesToFilter.forEach(name => {
                const yAxisValue = allYAxisValues.find(yAxisValue => yAxisValue.category == name);
                if (yAxisValue) {
                    result.push(yAxisValue);
                }
            });
        } else {
            result = allYAxisValues;
        }
        return result;
    }

    protected executeAdvancedSearch(advancedSearchBody: any): void {
        this.eventAdvancedSearchBody = advancedSearchBody;
        if (!this.inputs?.periodRef && advancedSearchBody.startTimestamp && advancedSearchBody.endTimestamp) {
            this.range = new DateRange(moment_tz.tz(advancedSearchBody.startTimestamp, this.timezone), moment_tz.tz(advancedSearchBody.endTimestamp, this.timezone));
        }
        delete this.eventAdvancedSearchBody.startTimestamp;
        delete this.eventAdvancedSearchBody.endTimestamp;
        if (this.advancedSearchInitCompleted) {
            this.loadChartData();
        } else {
            this.advancedSearchInitCompleted = true;
        }
    }

    closeDetails(): void {
        this.selectedData = null;
        this.detailsTitle = null;
        this.resetSelectedElementStyle();
    }

    private updateBeforePrint(): void {
        this.resizeChart();
    }

}

export class DailySummaryGridWidgetConfig {
    defaultPeriod: DateRangeName;
    showEventCount: boolean;
    showEventSeverity: boolean;
    sortByOccurrences: boolean;
    eventQuery: { property: string, predicate: string, value: any }[];
    eventNames: string;
    metricGroupNames: string;
    showMetricHighlightCount: boolean;
    showChangesOnly: boolean;
}

class ReferenceInput {
    periodRef: string;
    eventQueryRef: string;
}

export class ChartData {
    category: string;
    categoryLabel: string;
    dateTimestamp: string;
    value: ChartDataValue;
}

export class ChartDataValue {
    color: string;
    highlightCount: number;
    tooltipText: string;
    events: Alert[];
    metricData: MetricData[];
    metricDetailsArray: MetricDetails[];
}

export class MetricData {
    timestamp: number;
    metricName: string;
    metricLabel: string;
    value: any;
    unit: string;
    filter: any;
    filterArg: any;
    highlightColor: string;
}

export class XAxisValue {
    startTimestamp: number;
    endTimestamp: number;
}

export class YAxisValue {
    category: string;
    categoryLabel: string
}

export class DailySummaryGridWidgetMetricConfig {
    group: string;
    highlight: DailySummaryGridWidgetHighlightType;
}

export enum DailySummaryGridWidgetHighlightType {
    VALUE_CHANGE = 'VALUE_CHANGE',
    VALUE_SEVERITY = 'VALUE_SEVERITY'
}

export class MetricDetails {
    name: string;
    label: string;
    unit: string;
    filter: any;
    filterArg: any;
}